import React, {useState, useContext, useEffect} from 'react';
import '../../BidmiiCustomLoader/SmallAnimatedLoader.css';
import Grid from '@mui/material/Grid';
import Slide from '@mui/material/Slide';
import Typography from '@mui/material/Typography';
import ActionButtons from './ActionButtons';
import {LazyCKEditor} from '../../../common/LazyLoader';
import TertiaryButton from '../../../common/StyledComponents/TertiaryButton';
import {NewProjectContext} from '../utils/NewProjectContext';
import {generateProjectDescription} from '../../../util/AiTextGeneration';
import {useRecoilValue} from 'recoil';
import {API_URL} from '../../../constants';
import {securedFetchRequest} from '@bidmii/common/lib/util/FetchRequest';
import * as Sentry from '@sentry/react';
import {userState} from '../../../recoil/user/userAtom';
import useSnackbar from '../../../hooks/useSnackbar';
import Skeleton from '@mui/material/Skeleton';
import ModeEditIcon from '@mui/icons-material/ModeEdit';

interface IProps {
  changeStepHandler: (number: number) => void;
  forward: boolean;
  defaultValue: string;
}

const ProjectDescription = ({
  changeStepHandler,
  forward,
  defaultValue,
}: IProps) => {
  const user = useRecoilValue(userState);
  const {project, setProject, homeDepot} = useContext(NewProjectContext);

  const {openSnackbar} = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [AILoading, setAILoading] = useState(false);
  const [description, setDescription] = useState(defaultValue || '');
  const [mode, setMode] = useState<string>('view');

  const generateDescription = () => {
    setAILoading(true);
    generateProjectDescription(project.title, project.uid).then((res) => {
      setAILoading(false);
      if (res) {
        setDescription(res);
      } else {
        openSnackbar(
          'The server had an error while processing your request. Please try again.',
          'error',
        );
      }
    });
  };

  useEffect(() => {
    generateDescription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeHandler = (event: $TSFixMe, editor: $TSFixMe) => {
    const data = editor.getData();
    setDescription(data);
  };

  const handleClickNext = () => {
    setLoading(true);
    securedFetchRequest(`${API_URL}/rfps/${project.uid}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({description}),
    })
      .then((res) => res.json())
      .then((res) => {
        setLoading(false);
        localStorage.setItem('rfp', JSON.stringify(res));
        setProject(res);
        changeStepHandler(1);
      })
      .catch((err) => {
        setLoading(false);
        process.env.NODE_ENV === 'development' && console.error(err);

        Sentry.withScope(function (scope) {
          scope.setUser({
            uid: user?.uid,
            firstName: user?.firstName,
          });
          scope.setTag('section', 'create_user_step_2');
          Sentry.captureException(err);
        });
      });
  };

  const handleKeyDown = (event: $TSFixMe) => {
    if (event.keyCode === 13) {
      event.preventDefault(); // Ensure it is only this code that runs
      handleClickNext();
    }
  };

  return (
    <Slide in={true} direction={forward ? 'left' : 'right'} timeout={400}>
      <div style={{width: '100%', maxWidth: 450}}>
        <Typography
          variant="h6"
          fontSize={22}
          lineHeight={1.4}
          mb={1}
          fontWeight="bold">
          {AILoading ? 'Generating Summary' : 'Review summary'}
        </Typography>
        <Grid
          container
          justifyContent="flex-end"
          alignItems="center"
          style={{width: 'auto'}}>
          {AILoading ? (
            <div
              style={{
                display: 'flex',
                marginLeft: 4,
                marginRight: homeDepot ? 64 : 53,
                marginBottom: 10,
              }}>
              <div className="smallDot" />
              <div className="smallDot" />
              <div className="smallDot" />
              <div className="smallDot" />
              <div className="smallDot" />
            </div>
          ) : (
            <>
              {mode === 'view' && (
                <TertiaryButton
                  disabled={AILoading}
                  fontSize={14}
                  onClick={() => setMode('edit')}
                  bold>
                  Edit <ModeEditIcon fontSize="small" />
                </TertiaryButton>
              )}
            </>
          )}
        </Grid>

        {AILoading ? (
          <>
            <div>
              <Skeleton
                variant="text"
                width={'30%'}
                height={26}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 5}}
              />
              <Skeleton
                variant="text"
                width={'100%'}
                height={23}
                style={{marginBottom: 25}}
              />
            </div>
          </>
        ) : (
          <>
            {mode === 'view' ? (
              <div
                dangerouslySetInnerHTML={{__html: description}}
                onClick={() =>
                  setMode((prevState) =>
                    prevState === 'view' ? 'edit' : 'view',
                  )
                }
              />
            ) : (
              <LazyCKEditor
                //label="Project summary*"
                autoFocus={window.outerWidth > 500}
                id="new-project-step-one-description"
                data={description}
                config={{
                  toolbar: [],
                  placeholder: 'Generating project description...',
                }}
                onChange={(event: $TSFixMe, editor: $TSFixMe) =>
                  onChangeHandler(event, editor)
                }
                onKeyDown={handleKeyDown}
                homeDepot={homeDepot}
              />
            )}
            <ActionButtons
              secondaryAction={() => changeStepHandler(-1)}
              primaryAction={handleClickNext}
              loading={loading}
            />
          </>
        )}
      </div>
    </Slide>
  );
};

export default ProjectDescription;
